import {
  buildRefinementIconXPath,
  buildRefinementOriginalXPath,
  buildResultsContainerXPath,
  buildRowCheckboxInputXPath,
  buildRowCheckboxLabelXPath,
  buildRowXPathForCode,
  computeDelta,
  containerSpansMultipleLists,
  deltaSignature,
  findUnspecifiedChild,
  judgeCommit,
  parsePickerRows,
  parseRefinementOriginalRow,
  parseRootRow,
  snapshotFromRows,
} from "./pfDiagnosisPicker";

/**
 * Practice Fusion `pasteAndPickList` driver, orchestrated from the webapp on top of the
 * ALREADY-DEPLOYED extension's generic actions (`setFieldValue`+useCDP, `event`+useCDP,
 * `getDOM`, `existsElement`). No extension release is needed to ship or to hotfix it.
 *
 * CRITICAL: a result row's checkbox is a TOGGLE — clicking an aria-selected row REMOVES the
 * diagnosis from the encounter. Every click is preceded by a selection-state check, and an
 * already-selected row counts as matched WITHOUT clicking (idempotent re-sync). Anything we
 * could not read is "unknown", and unknown always means DO NOT CLICK.
 *
 * Returns the same `{matched, missed, cancelled}` contract the extension-side v1 returned,
 * so useEHR.js's toasts, sync-report rows and missed-reason taxonomy are unchanged.
 */

export const PF_DEFAULT_WAITS = {
  // Result-list render budget after typing (template default: action.waitAfterPasteMs).
  waitAfterPasteMs: 1500,
  // Post-click confirmation budget (template default: action.waitAfterClickMs).
  waitAfterClickMs: 5000,
  // Read the selection set as close to the click as possible: the delta is LEAST reliable
  // on exactly the mis-landed-click path if it waits out the full waitAfterClickMs first.
  deltaPollBudgetMs: 1500,
  deltaPollIntervalMs: 150,
  rowPollIntervalMs: 150,
  selectedPollIntervalMs: 150,
  verifyPollIntervalMs: 250,
  autoMapRowPollMs: 2000,
  // Refinement-panel flow: how long the panel's Search-match entry gets to render after the
  // icon click, and a larger delta budget for panel commits — the panel temporarily replaces
  // the results list, so the post-commit snapshot must ride out its close + re-render.
  refinementPanelBudgetMs: 3000,
  refinementPanelPollIntervalMs: 200,
  refinementDeltaBudgetMs: 4000,
};

const DISMISSED_ERROR = /debugging banner was dismissed/i;
const DETACHED_ERROR = /not attached|detached|debugger/i;
// A build shipped before commit 0e43931ff (2026-07-30 — see the comment on the 1.1.13 floor in
// useEHR.js) never introduced the DISMISSED_ERROR string above. On that older build a failed
// CDP attach instead surfaces as a RESOLVED (not thrown) `{error: "CDP error: ..."}` on a
// useCDP action. Both shapes mean the same thing operationally on this action family — CDP is
// unavailable and nothing was clicked — so a resolved error matching this shape on a useCDP
// action is treated as a dismissal too: one visible cancellation instead of grinding through
// every remaining code and re-popping the debugger banner once per code.
const OLD_BUILD_CDP_ERROR = /CDP error|debugger|not attached|detached/i;

class PfDismissedError extends Error {}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function isDismissedResponse(response, message) {
  if (!response || typeof response.error !== "string") return false;
  if (DISMISSED_ERROR.test(response.error)) return true;
  return !!(message && message.useCDP && OLD_BUILD_CDP_ERROR.test(response.error));
}

export function makeSender(sendRequest) {
  return async function send(message) {
    let response;
    try {
      response = await sendRequest(message);
    } catch (e) {
      const msg = (e && e.message) || "";
      // A detached debugger surfaces as a thrown transport error, not a tagged response —
      // treat it exactly like an explicit dismissal so the run stops instead of clicking on.
      if (DISMISSED_ERROR.test(msg) || DETACHED_ERROR.test(msg)) throw new PfDismissedError(msg);
      throw e;
    }
    if (isDismissedResponse(response, message)) throw new PfDismissedError(response.error);
    return response;
  };
}

// A response is a plausible element serialization when every entry is either a bare HTML
// string or an `{html: string}` object — the same tolerance toHtmlList (pfDiagnosisPicker.js)
// applies to legitimate multi-frame container reads. An array that mixes real HTML with a
// non-string entry is what a within-frame AMBIGUOUS match degrades to at the wire
// (lucasGetElementsByXPath returns an array of DOM NODES, not HTML, on snapshotLength > 1, and
// getDOM.js's `rootElement.outerHTML` on that array of nodes is undefined) — reject the whole
// payload as unreadable rather than silently keeping only the entries that happen to look
// valid.
function htmlOf(response) {
  if (response == null) return null;
  if (Array.isArray(response)) {
    if (response.length === 0) return null;
    const plausible = response.every((r) => typeof r === "string" || (r && typeof r.html === "string"));
    return plausible ? response : null;
  }
  if (typeof response.html === "string") return response;
  return null;
}

// Sentinel for a ROOT read whose selector matched more than one element. parsePickerRows
// legitimately MERGES a multi-entry container read (many frames can each contribute real
// rows), but a root read's whole contract is "the ONE element this selector resolved to" — so
// silently taking the first entry here (what parseRootRow would otherwise do) would hide
// exactly the duplicated-template-match precondition must-fix 2 exists to close. `code: null`
// so every existing "row not resolved" branch keeps working unmodified; the `ambiguous` flag
// lets the caller tell "not found" apart from "found more than once" and refuse to auto-map on
// the latter, since routing an ambiguous read into the substitute scanner would turn one
// silent misread into a more specific, still-undisclosed substitution.
const AMBIGUOUS_ROOT_ROW = { code: null, ambiguous: true };

async function readRootRow(send, xpath) {
  const response = await send({ action: "getDOM", root: { elementType: "path", element: xpath } });
  const html = htmlOf(response);
  if (html === null) return null;
  if (Array.isArray(html) && html.length > 1) return AMBIGUOUS_ROOT_ROW;
  return parseRootRow(html);
}

async function readContainerRows(send, containerXPath) {
  if (!containerXPath) return null;
  const response = await send({ action: "getDOM", root: { elementType: "path", element: containerXPath } });
  const html = htmlOf(response);
  if (html === null) return null;
  return { rows: parsePickerRows(html), html };
}

async function readSnapshot(send, containerXPath) {
  const read = await readContainerRows(send, containerXPath);
  if (!read) return null;
  return snapshotFromRows(read.rows);
}

/**
 * Poll `read()` until two CONSECUTIVE reads agree on their signature, or the budget runs
 * out. Practice Fusion renders the result list in more than one pass, so a single read can
 * catch a row mid-render — v1 guarded the same hazard with a geometry+sibling-count settle
 * check before trusting a click's coordinates. A read that reports `signature === null`
 * (nothing found yet) can never satisfy the rule.
 *
 * A stability verdict needs two reads by definition, so the budget only decides whether to
 * keep polling after a DISAGREEING pair — never whether the second read happens at all.
 * Checking the deadline after a single read let a slow first parse report a perfectly still
 * row as unsettled (CI #1522 under a 20ms test budget).
 */
async function pollStable(read, budgetMs, intervalMs) {
  const deadline = Date.now() + budgetMs;
  let prev = await read();
  for (;;) {
    await sleep(intervalMs);
    const next = await read();
    if (next.signature !== null && next.signature === prev.signature) return { ...next, stable: true };
    if (Date.now() >= deadline) return { ...next, stable: false };
    prev = next;
  }
}

function rowSignature(row, snapshot) {
  if (!row || row.code === null) return null;
  return JSON.stringify([
    row.code,
    row.selected,
    row.hasAriaSelected,
    row.needsRefinement,
    snapshot ? snapshot.codes : null,
    snapshot ? snapshot.rowCount : -1,
  ]);
}

async function pollForRow(send, rowXPath, containerXPath, budgetMs, intervalMs) {
  return pollStable(
    async () => {
      const row = await readRootRow(send, rowXPath);
      // Read the FULL container row list (not just the selected-codes snapshot) in the SAME
      // poll: commitRow's membership assert needs to know every rendered row's code, not only
      // which ones are selected, to tell "this row is inside the container the removal
      // detector watches" apart from "this row is selected".
      const containerRead = await readContainerRows(send, containerXPath);
      const snapshot = containerRead ? snapshotFromRows(containerRead.rows) : null;
      const containerRows = containerRead ? containerRead.rows : null;
      return { row, snapshot, containerRows, signature: rowSignature(row, snapshot) };
    },
    budgetMs,
    intervalMs
  );
}

async function pollRowSelected(send, rowXPath, budgetMs, intervalMs) {
  const deadline = Date.now() + budgetMs;
  for (;;) {
    const row = await readRootRow(send, rowXPath);
    if (row && row.selected) return true;
    if (Date.now() >= deadline) return false;
    await sleep(intervalMs);
  }
}

async function pollExists(send, xpath, budgetMs, intervalMs) {
  const deadline = Date.now() + budgetMs;
  for (;;) {
    const response = await send({ action: "existsElement", field: { elementType: "path", element: xpath } });
    if (response === true || (response && response.exists === true)) return true;
    if (Date.now() >= deadline) return false;
    await sleep(intervalMs);
  }
}

/**
 * Commit a refinable row's ORIGINAL code through the refinement panel. Opens the panel via
 * the row's own filter icon (the explicit aria-haspopup trigger — the row body's click
 * semantics differ between refinable and plain rows), waits for the panel's Search-match
 * entry for EXACTLY the requested code, re-asserts its parsed code (defense-in-depth on
 * transient UI), and clicks it. Returns null on success — the caller's shared delta/selected
 * verification remains the authority on what the click actually did — or a missed-reason
 * string on failure, with nothing clicked beyond the panel trigger.
 */
async function commitViaRefinementPanel(send, rowXPath, code, waits) {
  const opened = await send({
    action: "event",
    useCDP: true,
    element: { elementType: "path", element: buildRefinementIconXPath(rowXPath) },
  });
  // Distinct reason per failure branch — icon unresolvable, entry never rendered, and entry
  // unclickable are three different breakages (selector drift vs slow panel vs panel closed
  // mid-flow) and support triage needs to tell them apart from the reason string alone.
  if (opened == null) return "refinement_icon_click_failed";

  const matchXPath = buildRefinementOriginalXPath(code);
  const deadline = Date.now() + (waits.refinementPanelBudgetMs || 3000);
  let entry = null;
  for (;;) {
    const response = await send({ action: "getDOM", root: { elementType: "path", element: matchXPath } });
    const html = htmlOf(response);
    // An ambiguous multi-match read can never be "the one Search-match entry" — keep polling;
    // if it never resolves cleanly the deadline grades it visibly below.
    if (html !== null && !(Array.isArray(html) && html.length > 1)) {
      entry = parseRefinementOriginalRow(html);
      if (entry) break;
    }
    if (Date.now() >= deadline) return "refinement_panel_not_found";
    await sleep(waits.refinementPanelPollIntervalMs || 200);
  }
  // Invariant check on the parse itself: the XPath predicate should make disagreement
  // unreachable — if this ever fires, the selector and the parser no longer agree about the
  // panel's DOM (markup drift), and clicking on that disagreement is exactly what "never
  // clicked, never guessed" forbids.
  if (entry.code !== code) return "refinement_match_mismatch";

  const clicked = await send({
    action: "event",
    useCDP: true,
    element: { elementType: "path", element: matchXPath },
  });
  if (clicked == null) return "refinement_entry_click_failed";
  return null;
}

/**
 * Check-then-verify commit of one located result row. `code` is the code we EXPECT this row
 * to represent; `i` is the index into optionSelectors/verifySelectors, or -1 for an auto-map
 * row (whose XPath we built ourselves and for which no ground-truth selector exists).
 */
async function commitRow({ send, rowXPath, containerXPath, containerRows, row, before, code, i, ctx, extra }) {
  const { waits, verifySelectors, runState } = ctx;
  const miss = (reason, more) => ({
    matched: false,
    entry: { code, reason, elapsedMs: Date.now() - ctx.codeStart, ...more, ...extra },
  });

  // An unreadable row is "unknown", never a real "false" — "never clicked, never guessed"
  // applies whether or not the row would have needed the refinement panel. (Refinable rows
  // themselves are supported: their commit routes through the panel's Search-match entry —
  // see the needsRefinement branch below.)
  if (!row) return miss("refinement_state_unknown");

  // One-time-per-run assertion that the resolved element actually carries an aria-selected
  // attribute at all. Without it, an optionSelectorTemplate anchored on a wrapper (not the
  // li[role="option"] itself) makes the toggle-safety read below permanently and silently
  // "false" — indistinguishable from "not selected" until a real click lands on a live row.
  if (!runState.ariaSelectedAsserted) {
    if (!row.hasAriaSelected) {
      // Only a TEMPLATE-RESOLVED row may condemn the template. An auto-map row (i = -1) is
      // anchored on li[@role="option"] by construction and would always satisfy the
      // assertion trivially; letting it consume the one-time assertion would leave a
      // mis-anchored optionSelectorTemplate unasserted for every later template row.
      if (i >= 0) {
        console.error(
          "LucasAI pasteAndPickListPF: resolved row has no aria-selected attribute — " +
            "optionSelectorTemplate is likely mis-anchored (points at a wrapper, not the " +
            'li[role="option"] itself). Refusing to click any row for the rest of this run.'
        );
        runState.templateMisconfigured = true;
        return miss("template_aria_selected_missing");
      }
      return miss("aria_state_unknown");
    }
    if (i >= 0) runState.ariaSelectedAsserted = true;
  }

  // Toggle safety, tri-state. An ABSENT aria-selected attribute is "unknown", never a
  // well-formed "false" — and false here means CLICK, which on a toggle is a silent
  // deletion of a recorded diagnosis reported to the provider as a benign "missed".
  if (!row.hasAriaSelected) return miss("selection_state_unknown");

  // Pre-click identity assert — MUST run before the toggle-safety already-selected path just
  // below returns `matched`, and before any click. `optionSelectorTemplate` is user-authored
  // (`buildIcd10OptionSelectors` is a blind `${code}` substitution with no code-anchoring
  // guarantee), so a template anchored on an index attribute instead of the code can resolve
  // EVERY requested code to the SAME row: code 1 would click it (a wrong ICD-10 committed to
  // the chart) and, once that row is aria-selected, every later code would hit the
  // already-selected branch and be reported "matched" without ever being clicked — a
  // wrong-direction signal on the channel clinicians trust least skeptically. `row.isOption`
  // (parseRootRow computes it; nothing previously read it) is the direct check for a template
  // anchored on a wrapper around the row instead of the li[role="option"] itself. Auto-map rows
  // satisfy both trivially: tryAutoMap re-reads the row immediately before calling commitRow
  // and passes `code = child.code`, the same code that fresh read already confirmed, off a
  // selector built by buildRowXPathForCode (always li[@role="option"]).
  if (row.code !== code || !row.isOption) return miss("automap_row_mismatch");

  if (!row.selected) {
    // Positive post-commit assertion (compensating control while verifySelectorTemplate is
    // null): without a readable baseline — or with a baseline that sees no rendered rows at
    // all while we hold a resolved row — any delta computed on top of it is meaningless.
    // Refuse to click.
    if (!before || before.rowCount === 0) return miss("commit_delta_unknown");

    // Scope the click to what the removal detector watches. `getDOM` (content script,
    // document.evaluate, same-origin frames, first match) and the trusted click
    // (`DOM.performSearch {pierce:true}`, all frames/shadow roots, first VISIBLE match) can
    // resolve the SAME xpath to different elements when a second/stale composable-select is on
    // the page — and computeDelta only ever sees `buildResultsContainerXPath`'s scope, so a
    // click that lands outside it grades a real deletion as the benign
    // `checkbox_did_not_select`. Refuse before clicking at all when this poll's own container
    // read never saw the row (membership assert), and — belt and suspenders — bind the actual
    // click to a container-scoped, code-anchored xpath rather than the raw template locator,
    // the same anchoring tryAutoMap already uses for its own click.
    if (!containerRows || !containerRows.some((r) => r.code === row.code)) {
      return miss("row_outside_results_container");
    }
    const clickRowXPath = buildRowXPathForCode(containerXPath, row.code);

    if (row.needsRefinement) {
      // A refinable row's checkbox does not commit — Practice Fusion opens the refinement
      // panel instead (live-confirmed 2026-08-21). The panel's "Search match" section offers
      // the ORIGINAL code as its own committable entry, so the requested code is committed
      // exactly — never a refinement substitute. The shared delta/selected verification
      // below remains the authority on what actually happened.
      const panelFailure = await commitViaRefinementPanel(send, clickRowXPath, code, waits);
      if (panelFailure) return miss(panelFailure);
    } else {
      let clicked = await send({
        action: "event",
        useCDP: true,
        element: { elementType: "path", element: buildRowCheckboxLabelXPath(clickRowXPath) },
      });
      if (clicked == null) {
        clicked = await send({
          action: "event",
          useCDP: true,
          element: { elementType: "path", element: buildRowCheckboxInputXPath(clickRowXPath) },
        });
      }
      if (clicked == null) return miss("checkbox_not_found");
      // A click that reported an error still gets verified rather than trusted: the delta
      // poll below is the authority on what actually happened to the chart.
    }

    // STABILITY rule: a non-empty delta only ends the poll once TWO CONSECUTIVE reads agree
    // on it. A single snapshot taken mid-render can transiently show a previously-selected
    // row as unrendered, which would fire the clinical "diagnosis removed" alarm for a
    // commit that is clean 150ms later. Panel commits get a larger budget: the panel
    // temporarily replaces the results list, and those transitional reads (no rows) can
    // never satisfy the stability rule, so the poll must outlive the close + re-render.
    const deltaBudgetMs = row.needsRefinement
      ? Math.max(waits.deltaPollBudgetMs, waits.refinementDeltaBudgetMs || 0)
      : waits.deltaPollBudgetMs;
    let afterSnap = await readSnapshot(send, containerXPath);
    let prevSig = afterSnap && afterSnap.rowCount > 0 ? deltaSignature(before, afterSnap) : null;
    const deltaDeadline = Date.now() + deltaBudgetMs;
    while (Date.now() < deltaDeadline) {
      await sleep(waits.deltaPollIntervalMs);
      const next = await readSnapshot(send, containerXPath);
      const sig = next && next.rowCount > 0 ? deltaSignature(before, next) : null;
      if (sig !== null && sig !== "|" && sig === prevSig) {
        afterSnap = next;
        break;
      }
      prevSig = sig;
      afterSnap = next;
    }

    // Teardown discriminator: a failed read, or zero rendered diagnosis rows, already
    // determines the outcome — report "unknown" NOW rather than after a pointless
    // waitAfterClickMs wait.
    if (!afterSnap || afterSnap.rowCount === 0) return miss("commit_delta_unknown");

    // The row's own signal is consulted as a VALUE, never as a gate before the delta: a
    // mis-landed click on a NEIGHBOURING row leaves the intended row's aria-selected false
    // while still mutating the selection set. A stability-confirmed dirty delta is judged
    // immediately; otherwise the long wait runs, followed by one FINAL authoritative
    // snapshot — unconditional, so a late mishap during the wait (a knock-on de-selection
    // seconds after a clean-looking early add) is still judged on real data.
    let selected = false;
    const early = computeDelta(before, afterSnap);
    const earlyDirty =
      early.removed.length > 0 || (early.added.length > 0 && (early.added.length !== 1 || early.added[0] !== code));
    if (!earlyDirty) {
      selected = await pollRowSelected(send, clickRowXPath, waits.waitAfterClickMs, waits.selectedPollIntervalMs);
      afterSnap = await readSnapshot(send, containerXPath);
    }

    const verdict = judgeCommit({ before, after: afterSnap, code, selected });
    if (!verdict.matched) {
      // judgeCommit's benign fallback is named for the checkbox path; on a panel commit no
      // checkbox was ever clicked, and the same outcome (clean delta, row never selected)
      // means the panel's Search-match click never landed — name it so triage looks at the
      // panel flow, not checkbox selectors.
      if (row.needsRefinement && verdict.reason === "checkbox_did_not_select") {
        return miss("refinement_commit_did_not_land");
      }
      if (verdict.reason === "commit_removed_existing_code") {
        // Code-free and codes-free by design: captureConsoleIntegration({levels:"error"}) +
        // sendDefaultPii:true turns every webapp console.error into a Sentry EVENT, and
        // sanitizeSentryEvent truncates event.message at 2048 chars but never redacts it — so
        // a JSON.stringify of the patient's removed ICD-10 codes here would ship a real
        // diagnosis set to a third party correlated with the clinician's user id, IP and the
        // encounter URL. removedCodes already reaches the provider on the returned entry, the
        // removal toast, and the sync report (useEHR.js) over the app's own authenticated
        // channel — this line only needs to be a loud LOCAL signal that something happened.
        console.error(
          "LucasAI pasteAndPickListPF: a commit removed " +
            verdict.removedCodes.length +
            " previously-selected code(s) from the picker — a diagnosis may have been deleted " +
            "from the encounter. See the removal toast / sync report for which code(s)."
        );
        return miss(verdict.reason, { removedCodes: verdict.removedCodes });
      }
      return miss(verdict.reason);
    }
  }

  // Ground truth (optional, template-configured): the code must appear in the committed
  // ICD-10 DIAGNOSES list, outside the picker. Skipped for auto-mapped rows — verifySelectors
  // was built for the ORIGINAL code, so there is no ground-truth selector for the substitute.
  if (i >= 0 && verifySelectors && verifySelectors[i]) {
    const verified = await pollExists(send, verifySelectors[i], waits.waitAfterClickMs, waits.verifyPollIntervalMs);
    if (!verified) return miss("not_in_diagnoses_after_commit");
  }

  return { matched: true, entry: { code, elapsedMs: Date.now() - ctx.codeStart, ...extra } };
}

async function tryAutoMap({ send, containerXPath, code, ctx }) {
  const { waits } = ctx;
  const read = await readContainerRows(send, containerXPath);
  // No readable container means we could not safely bound the scan; a document-wide scan
  // could be answered by a duplicate/stale composable-select widget elsewhere on the page,
  // so skip auto-map rather than widen it. Same for a container spanning two result lists.
  if (!read || containerSpansMultipleLists(read.html)) return null;

  const child = findUnspecifiedChild(read.rows, code);
  if (!child) return null;

  const childXPath = buildRowXPathForCode(containerXPath, child.code);
  const located = await pollForRow(send, childXPath, containerXPath, waits.autoMapRowPollMs, waits.rowPollIntervalMs);
  if (!located.row || located.row.code === null) return null;

  // Clinical-safety re-assert (mirrors AdvancedMD's scope-code assertion): the row we are
  // about to click must, on a FRESH read, still display exactly the child code the scanner
  // chose.
  const fresh = await readRootRow(send, childXPath);
  if (!fresh || fresh.code !== child.code) {
    return {
      matched: false,
      entry: { code, reason: "automap_row_mismatch", elapsedMs: Date.now() - ctx.codeStart },
    };
  }

  return commitRow({
    send,
    rowXPath: childXPath,
    containerXPath,
    containerRows: located.containerRows,
    row: fresh,
    before: located.snapshot,
    code: child.code,
    i: -1,
    ctx,
    extra: {
      originalCode: code,
      viaParentAutoPick: true,
      autoPickReason: "exact_code_not_in_picker:" + child.matchType,
      autoPickDescription: child.description,
    },
  });
}

export async function runPfPasteAndPickList({
  sendRequest,
  codes,
  optionSelectors,
  verifySelectors,
  searchInput,
  autoMapParents,
  waits,
}) {
  const result = { matched: [], missed: [], cancelled: false };
  const list = Array.isArray(codes) ? codes : [];
  const budgets = { ...PF_DEFAULT_WAITS, ...(waits || {}) };
  const send = makeSender(sendRequest);
  const containerXPath = buildResultsContainerXPath(searchInput && searchInput.element);

  // Per-RUN state, deliberately NOT module-level: concurrent runs must not share (or reset)
  // each other's refuse-to-click safety latch.
  const runState = { ariaSelectedAsserted: false, templateMisconfigured: false };

  try {
    for (let i = 0; i < list.length; i++) {
      // A mis-anchored optionSelectorTemplate is a template-wide defect, not a per-code
      // fluke — retrying it on every remaining code would just repeat the same unsafe read.
      if (runState.templateMisconfigured) {
        for (let j = i; j < list.length; j++) {
          result.missed.push({ code: list[j], reason: "template_aria_selected_missing", elapsedMs: 0 });
        }
        break;
      }

      const code = list[i];
      const codeStart = Date.now();
      const ctx = { waits: budgets, verifySelectors, runState, codeStart };

      try {
        const typed = await send({
          action: "setFieldValue",
          useCDP: true,
          field: { elementType: searchInput.elementType, element: searchInput.element, value: code },
        });
        if (typed == null) {
          result.missed.push({ code, reason: "search_input_not_found", elapsedMs: Date.now() - codeStart });
          continue;
        }
        if (!typed.success) {
          result.missed.push({ code, reason: "type_failed", elapsedMs: Date.now() - codeStart });
          continue;
        }

        const rowXPath = (optionSelectors || [])[i];
        const located = rowXPath
          ? await pollForRow(send, rowXPath, containerXPath, budgets.waitAfterPasteMs, budgets.rowPollIntervalMs)
          : { row: null, snapshot: null, stable: false };

        if (!located.row || located.row.code === null) {
          // An AMBIGUOUS root read (the template's xpath matched more than one element) is NOT
          // "not found" — it is its own definitive, visible outcome and must never fall through
          // into the auto-map fallback. tryAutoMap runs its own independent, legitimately-
          // scoped container scan and could find a real substitute candidate there, turning one
          // unreadable signal into a more specific, still-undisclosed substitution.
          if (located.row && located.row.ambiguous) {
            result.missed.push({ code, reason: "ambiguous_row_match", elapsedMs: Date.now() - codeStart });
            continue;
          }
          // Exact code not in the results — try the parent→unspecified-child auto-map.
          const outcome = autoMapParents === false ? null : await tryAutoMap({ send, containerXPath, code, ctx });
          if (!outcome) {
            result.missed.push({ code, reason: "code_not_found_in_results", elapsedMs: Date.now() - codeStart });
          } else if (outcome.matched) {
            result.matched.push(outcome.entry);
          } else {
            result.missed.push(outcome.entry);
          }
          continue;
        }

        // The row rendered but never held still across two reads: PF is mid-render, and a
        // click aimed at it can land on whatever takes its place.
        if (!located.stable) {
          result.missed.push({ code, reason: "row_not_settled", elapsedMs: Date.now() - codeStart });
          continue;
        }

        const outcome = await commitRow({
          send,
          rowXPath,
          containerXPath,
          containerRows: located.containerRows,
          row: located.row,
          before: located.snapshot,
          code,
          i,
          ctx,
        });
        if (outcome.matched) result.matched.push(outcome.entry);
        else result.missed.push(outcome.entry);
      } catch (perCodeErr) {
        if (perCodeErr instanceof PfDismissedError) {
          result.missed.push({ code, reason: "cdp_user_dismissed", elapsedMs: Date.now() - codeStart });
          result.cancelled = true;
          for (let j = i + 1; j < list.length; j++) {
            result.missed.push({ code: list[j], reason: "cdp_user_dismissed", elapsedMs: 0 });
          }
          break;
        }
        result.missed.push({
          code,
          reason: "exception:" + ((perCodeErr && perCodeErr.message) || ""),
          elapsedMs: Date.now() - codeStart,
        });
      }
    }

    console.log("LucasAI pasteAndPickListPF SUMMARY matched=" + result.matched.length + " missed=" + result.missed.length);
    return result;
  } catch (e) {
    return { ...result, error: "pasteAndPickListPF failed: " + ((e && e.message) || "") };
  }
}
